<script setup lang="ts">
import { computed, ref, onBeforeUnmount } from 'vue';
import BaseIcon, { type IconName } from './BaseIcon.vue';

type ButtonVariant = 'primary' | 'success' | 'danger';
type ButtonSize = 'small' | 'medium' | 'large';

const HOLD_DURATION_MS = 2000;

const props = withDefaults(defineProps<{
  variant?: ButtonVariant;
  size?: ButtonSize;
  disabled?: boolean;
  loading?: boolean;
  leftIcon?: IconName;
  rightIcon?: IconName;
  holdToConfirm?: boolean;
}>(), {
  variant: 'primary',
  size: 'medium',
  disabled: false,
  loading: false,
  holdToConfirm: false
});

const emit = defineEmits<{
  click: [event: MouseEvent]
}>();

const isHolding = ref(false);
const holdProgress = ref(0);
let raf = 0;
let t0 = 0;

const buttonClass = computed(() => [
  'base-button',
  `base-button--${props.variant}`,
  `base-button--${props.size}`,
  {
    'base-button--disabled': props.disabled || props.loading,
    'base-button--loading': props.loading,
    'base-button--holding': isHolding.value
  }
]);

function stopHold() {
  isHolding.value = false;
  holdProgress.value = 0;
  cancelAnimationFrame(raf);
}

function tick() {
  const dt = performance.now() - t0;
  holdProgress.value = Math.min(dt / HOLD_DURATION_MS, 1);

  if (holdProgress.value >= 1) {
    stopHold();
    emit('click', new MouseEvent('click'));
    return;
  }

  raf = requestAnimationFrame(tick);
}

function startHold() {
  if (props.disabled || props.loading) return;

  isHolding.value = true;
  t0 = performance.now();
  raf = requestAnimationFrame(tick);
}

function handleClick(event: MouseEvent) {
  if (props.disabled || props.loading) return;

  // If holdToConfirm is enabled, don't emit on regular click
  if (props.holdToConfirm) {
    return;
  }

  emit('click', event);
}

onBeforeUnmount(() => stopHold());
</script>

<template>
  <button
    :class="buttonClass"
    :disabled="disabled || loading"
    @click="handleClick"
    @pointerdown.prevent="holdToConfirm && startHold()"
    @pointerup.prevent="holdToConfirm && stopHold()"
    @pointercancel.prevent="holdToConfirm && stopHold()"
    @pointerleave.prevent="holdToConfirm && stopHold()"
  >
    <!-- Hold progress overlays - from both sides -->
    <span
      v-if="holdToConfirm"
      class="base-button__hold-progress base-button__hold-progress--left"
      :style="{ transform: `scaleX(${holdProgress})` }"
    ></span>
    <span
      v-if="holdToConfirm"
      class="base-button__hold-progress base-button__hold-progress--right"
      :style="{ transform: `scaleX(${holdProgress})` }"
    ></span>

    <span v-if="loading" class="base-button__spinner"></span>

    <template v-else>
      <BaseIcon
        v-if="leftIcon"
        :name="leftIcon"
        class="base-button__icon base-button__icon--left"
        color="currentColor"
      />

      <span class="base-button__text">
        <slot></slot>
      </span>

      <BaseIcon
        v-if="rightIcon"
        :name="rightIcon"
        class="base-button__icon base-button__icon--right"
        color="currentColor"
      />
    </template>
  </button>
</template>

<style scoped>
.base-button {
  position: relative;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-xs);
  border: none;
  border-radius: var(--border-radius);
  cursor: pointer;
  font-size: var(--font-size-md);
  font-weight: 500;
  transition: opacity 0.2s, transform 0.1s;
  font-family: inherit;
  overflow: hidden;
  -webkit-user-select: none;
  user-select: none;
  -webkit-touch-callout: none;
  touch-action: manipulation;
}


/* Sizes - only padding changes */
.base-button--small {
  padding: var(--spacing-xs) var(--spacing-sm);
}

.base-button--medium {
  padding: var(--spacing-sm) var(--spacing-md);
}

.base-button--large {
  padding: var(--spacing-md) var(--spacing-lg);
}

/* Variants - different colors */
.base-button--primary {
  background-color: var(--color-btn-dark-bg);
  color: var(--color-btn-dark-text);
}

.base-button--success {
  background-color: var(--color-btn-active-bg);
  color: var(--color-btn-active-text);
}

.base-button--danger {
  background-color: var(--color-btn-cancel-bg);
  color: var(--color-btn-cancel-text);
}

/* States */
.base-button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.base-button:hover:not(.base-button--disabled) {
  opacity: 0.9;
}

/* Icon styling */
.base-button__icon {
  display: inline-flex;
  align-items: center;
  font-size: 1.2em;
}

.base-button__icon--left {
  padding-right: var(--spacing-xs);
}

.base-button__icon--right {
  padding-left: var(--spacing-xs);
}

/* Text wrapper */
.base-button__text {
  display: inline-flex;
  align-items: center;
  pointer-events: none;
}

/* Loading spinner */
.base-button__spinner {
  display: inline-block;
  width: 1em;
  height: 1em;
  border: 2px solid currentColor;
  border-right-color: transparent;
  border-radius: 50%;
  animation: spin 0.6s linear infinite;
}

/* Hold progress bars - from both sides */
.base-button__hold-progress {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 50%;
  background-color: rgba(255, 255, 255, 0.3);
  transform: scaleX(0);
  pointer-events: none;
  z-index: 0;
}

.base-button__hold-progress--left {
  left: 0;
  transform-origin: left center;
}

.base-button__hold-progress--right {
  right: 0;
  transform-origin: right center;
}

.base-button--holding .base-button__hold-progress {
  transition: none;
}

/* Ensure content is above progress bar */
.base-button__text,
.base-button__icon,
.base-button__spinner {
  position: relative;
  z-index: 1;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
